import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, Mountain } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const { verifyRecoveryEmail, completePasswordRecovery } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const verificationRequest = useRef(null);
  const [status, setStatus] = useState("checking");
  const [resetToken, setResetToken] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setError("Recovery link is missing a token.");
      return;
    }
    let alive = true;
    if (verificationRequest.current?.token !== token) {
      verificationRequest.current = { token, promise: verifyRecoveryEmail(token) };
    }
    verificationRequest.current.promise
      .then((data) => {
        if (!alive) return;
        setResetToken(data.reset_token);
        setStatus("ready");
      })
      .catch((err) => {
        if (!alive) return;
        setStatus("error");
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      });
    return () => { alive = false; };
  }, [params, verifyRecoveryEmail]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true); setError("");
    try {
      await completePasswordRecovery(resetToken, password);
      setPassword(""); setResetToken(""); navigate("/", { replace: true });
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="tp-app-shell text-[#F4EFEB] antialiased" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
        <Link to="/landing" className="mb-8 flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#E5B85C]">
            <Mountain className="h-5 w-5 text-[#17211D]" />
          </span>
          <span className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>TrailPulse</span>
        </Link>

        <section className="tp-dark-panel rounded-[2rem] p-8">
          {status === "checking" ? (
            <div className="py-8 text-center">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#E5B85C]" />
              <h1 className="mt-5 text-2xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>Checking link</h1>
            </div>
          ) : status === "ready" ? (
            <form onSubmit={submit}>
              <CheckCircle2 className="h-8 w-8 text-[#3FA98C]" />
              <h1 className="mt-5 text-2xl font-bold tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>New password</h1>
              <p className="mt-1 text-sm text-[#94A3B8]">Link verified. Set your password and log in.</p>
              <label htmlFor="reset-password" className="mt-6 block text-xs font-medium text-[#94A3B8]">New password</label>
              <input id="reset-password" type="password" autoComplete="new-password" placeholder="Password" value={password} disabled={loading}
                onChange={(e) => setPassword(e.target.value)} required minLength={8}
                className="mt-1.5 min-h-10 w-full rounded-xl border border-white/15 bg-[#FFFAF1]/10 px-4 py-2.5 text-[#F4EFEB] outline-none transition-colors focus:border-[#E5B85C] focus:ring-2 focus:ring-[#E5B85C]" />
              {error && <div className="mt-4 rounded-lg border border-[#E5695B]/30 bg-[#E5695B]/10 px-3 py-2 text-sm text-[#E5695B]">{error}</div>}
              <button disabled={loading} type="submit" className="mt-6 flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-[#E5B85C] py-3 font-bold text-[#17211D] disabled:opacity-60">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Logging in...</> : "Login"}
              </button>
            </form>
          ) : (
            <div className="text-center">
              <h1 className="text-2xl font-bold tracking-tight text-[#E5695B]" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>Link unavailable</h1>
              <p className="mt-2 text-sm text-[#94A3B8]">{error}</p>
              <Link to="/login?recovery=1" className="mt-6 flex min-h-11 w-full items-center justify-center rounded-full bg-[#E5B85C] py-3 font-bold text-[#17211D]">Request a new link</Link>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
